import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { api } from '../../../convex/_generated/api';
import { BaseContext } from '../../../shared/base-context';
import { Id } from '../../../convex/_generated/dataModel';
import { MainRoutes } from '../../../shared/models';
import { Button } from '../../../catalyst-components/button';

export type ChatContextType =
  | 'skill'
  | 'skill_summary'
  | 'progress_summary'
  | 'session_review'
  | 'mindset';

interface ChatAboutButtonProps {
  contextType: ChatContextType;
  contextId: string;
  systemMessage: string;
  autoSubmitPrompt?: string;
  metadata?: {
    skill_id?: Id<"skills">;
    session_id?: Id<"sessions">;
  };
  label?: string;
  variant?: 'button' | 'icon' | 'text';
  className?: string;
}

export const ChatAboutButton = ({
  contextType,
  contextId,
  systemMessage,
  autoSubmitPrompt,
  metadata,
  label = 'Chat about this',
  variant = 'button',
  className = '',
}: ChatAboutButtonProps) => {
  const navigate = useNavigate();
  const { convexUserId, setIsSidebarShown } = useContext(BaseContext);
  const createPendingContext = useMutation(api.evergreen_pending_context.createPendingContext);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers

    if (!convexUserId) return;

    // Create pending context for evergreen chat
    await createPendingContext({
      user_id: convexUserId as Id<"users">,
      context_type: contextType,
      context_id: contextId,
      system_message: systemMessage,
      auto_submit_prompt: autoSubmitPrompt,
      metadata: metadata,
    });

    // Close sidebar if open, then navigate to evergreen chat
    setIsSidebarShown(false);
    navigate(MainRoutes.MyChat);
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`p-2 rounded-full hover:bg-primary/10 transition-colors ${className}`}
        title={label}
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" />
      </button>
    );
  }

  if (variant === 'text') {
    return (
      <button
        onClick={handleClick}
        className={`text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1.5 transition-colors ${className}`}
      >
        <ChatBubbleLeftRightIcon className="h-4 w-4" />
        {label}
      </button>
    );
  }

  return (
    <Button
      color="secondary"
      onClick={handleClick}
      className={`flex items-center gap-2 ${className}`}
    >
      <ChatBubbleLeftRightIcon className="h-4 w-4" />
      {label}
    </Button>
  );
};
